import React, { useEffect, useState } from "react";
import { setApiKey, setAcceptLanguageHeader, convertToi18nLanguage } from "./api";
import { MetaforceContext } from "./contexts"
import { I18nextProvider } from 'react-i18next';
import i18n from './translations/i18n';

import './tailwind.css'
import { useParams } from "react-router";

/** @typedef {{ startParameter: string, jobId?: string, docId: string, publishedAttachmentId?: string, apiBaseUrl: string, internalApiKey: string, jwt?: string }} Configurations */
/** @typedef {{ predefined?: ActionButton, library?: ActionButton, preview?: ActionButton, publish?: ActionButton, discard?: ActionButton, undo?: ActionButton, redo?: ActionButton, splitScreen?: ActionToggleButton }} ActionButtons */
/** @typedef {{ actionButtons?: ActionButtons, hideTopBar?: boolean, hideContextMenu?: boolean, removeMarginAndPadding?: boolean, hideActionButtonText?: boolean }} ThemeAndStyle */
/** @typedef {{ hide?: boolean, icon?: JSX.Element, text?: string, className?: string }} ActionButton */
/** @typedef {{ hide?: boolean, onIcon?: JSX.Element, offIcon?: JSX.Element, onText?: string, offText?: string, className?: string }} ActionToggleButton */
/** @typedef {{ message: string, customErrorMessage: string, validationErrors: string[], error: string }} CustomError */
/** @typedef {{ }} SuccessAfterFailedToSave */

/**
 * @param {Object}  props
 * @param {Configurations}  props.configurations
 * @param {React.ReactNode}  props.children
 * @param {() => Void}  [props.discard]
 * @param {() => Void}  [props.publish]
 * @param {(CustomError: CustomError) => Void}  [props.customErrorHandler]
 * @param {(SuccessAfterFailedToSave: SuccessAfterFailedToSave) => Void}  [props.successAfterFailedToSaveHandler]
  * @param {( message: string, type: ('success'|'error') ) => Void}  [props.toastHandler]
 * @param {ThemeAndStyle}  [props.themeAndStyle]
 * @param {()=> void}[props.onOneTimeJwtUsed]
 * @param {(startParameter: string) => Void}  [props.loadNewDocument]
 */
export const MetaforceProvider = ({ configurations, children, discard, publish, customErrorHandler, successAfterFailedToSaveHandler, toastHandler, themeAndStyle, onOneTimeJwtUsed }) => {
    const [startParameterState, setStartParameterState] = useState(configurations?.startParameter || "NO START PARAMETER SET");

    useEffect(() => {
        if(configurations?.startParameter != startParameterState) {
            setStartParameterState(configurations?.startParameter)
        }
    }, [configurations?.startParameter]);

    window._env_ = {
        REACT_APP_API_BASE_URL: configurations?.apiBaseUrl || "NO API BASE URL SET",
        REACT_APP_INTERNAL_API_KEY: configurations?.internalApiKey || "NO INTERNAL API KEY SET"
    };

    setApiKey();

    setAcceptLanguageHeader(convertToi18nLanguage(i18n.resolvedLanguage));

    return (
        <MetaforceContext.Provider value={{
            apiBaseUrl: window._env_?.REACT_APP_API_BASE_URL,
            internalApiKey: window._env_?.REACT_APP_INTERNAL_API_KEY,
            startParameter: startParameterState,
            loadNewDocument: (newStartParameter) => setStartParameterState(newStartParameter),
            jobId: configurations?.jobId || "NO JOB ID SET",
            docId: configurations?.docId || "NO DOC ID SET",
            publishedAttachmentId: configurations?.publishedAttachmentId || "NO PUBLISHED ATTACHMENT ID SET",
            discard: discard,
            publish: publish,
            onOneTimeJwtUsed: onOneTimeJwtUsed,
            jwt: configurations?.jwt,
            themeAndStyle: themeAndStyle,
            customErrorHandler: customErrorHandler,
            toastHandler: toastHandler || ((message, type) => console.log(`Toast: ${message} - ${type}`)),
            successAfterFailedToSaveHandler: successAfterFailedToSaveHandler || (() => console.log(`Success after failed to save!`))
        }}>
            <div className="tailwind-preflight">
                <I18nextProvider i18n={i18n}>
                    {children}
                </I18nextProvider>
            </div>
        </MetaforceContext.Provider>
    )
}